/**
 * FallbackEditorModal (Phase 23.1.6)
 *
 * Minimal trainer fallback editor for directly authoring a manual SOP
 * proposal. This is deliberately the de-prioritized escape hatch; the
 * normative authoring motion is AI-proposes / trainer-approves (see
 * blueprint §3.1). The modal ships "shell only" in 23.1.6 — inline-editable
 * text for section / task / step fields, an image uploader STUB, and a
 * "Generate as draft proposal" action that POSTs to
 * /api/sops/:sopId/proposals/manual.
 *
 * Flow:
 *   1. onOpen: POST /edit-lock/acquire. On 409 → show "other user editing"
 *      banner; submit is disabled.
 *   2. Trainer edits step/task/section text inline. Each edit is tracked as
 *      a structured operation (step_update / task_update / section_update)
 *      matching the blueprint §5.0 SopProposalChanges contract.
 *   3. Submit: trainer provides a rationale, the accumulated ops are posted
 *      to /proposals/manual with source='manual'.
 *   4. onClose: DELETE /edit-lock (best-effort). If the trainer's browser
 *      crashes, the lock auto-expires in
 *      LIVING_SOPS_CONFIG.MANUAL_EDITOR.LOCK_DURATION_MINUTES.
 *
 * Not implemented in 23.1.6 (deferred to downstream chunks):
 *   - step_insert / step_delete / step_reorder (23.2.2 redline UI adds these)
 *   - task_insert / task_delete / section_* operations
 *   - Real image upload — button stubbed with tooltip "Available after
 *     Chunk 23.4 ships evidence harvesting"
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ImagePlus,
  Info,
  Loader2,
  Lock,
  Save,
  X,
} from 'lucide-react';
import type {
  SopDetail,
  SopProposalChanges,
  SopProposalOperation,
} from '@guild/shared';
import {
  useAcquireEditLock,
  useCreateManualProposal,
  useReleaseEditLock,
} from '../../../hooks/useSopProposal';
import { ApiError } from '../../../lib/api';

interface FallbackEditorModalProps {
  sop: SopDetail;
  onClose: () => void;
}

// Tracks edits by reference key. We collect keyed overrides per entity so the
// submit handler can synthesize a SopProposalChanges operations[] array.
interface EditState {
  sections: Record<
    string,
    { title?: string; description?: string; whenToDoTrigger?: string }
  >;
  tasks: Record<string, { title?: string; description?: string }>;
  steps: Record<
    string,
    { description?: string; isSafetyCritical?: boolean }
  >;
}

const EMPTY_EDITS: EditState = { sections: {}, tasks: {}, steps: {} };

interface LockState {
  status: 'acquiring' | 'acquired' | 'conflict' | 'error';
  expiresAt?: string;
  otherUser?: { id: string; name: string } | null;
  errorMessage?: string;
}

export function FallbackEditorModal({ sop, onClose }: FallbackEditorModalProps) {
  const acquireLock = useAcquireEditLock(sop.id);
  const releaseLock = useReleaseEditLock(sop.id);
  const createProposal = useCreateManualProposal(sop.id);

  const [lock, setLock] = useState<LockState>({ status: 'acquiring' });
  const [edits, setEdits] = useState<EditState>(EMPTY_EDITS);
  const [rationale, setRationale] = useState('');
  const [submitState, setSubmitState] = useState<
    { kind: 'idle' } | { kind: 'submitting' } | { kind: 'success' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Acquire the edit lock when the modal opens. Done once per mount.
  useEffect(() => {
    let cancelled = false;
    acquireLock
      .mutateAsync()
      .then((res) => {
        if (cancelled) return;
        setLock({ status: 'acquired', expiresAt: res.expiresAt });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 409) {
          const details = (err.details ?? {}) as {
            otherUser?: { id: string; name: string } | null;
            expiresAt?: string;
          };
          setLock({
            status: 'conflict',
            otherUser: details.otherUser ?? null,
            expiresAt: details.expiresAt,
          });
          return;
        }
        setLock({
          status: 'error',
          errorMessage:
            err instanceof Error ? err.message : 'Failed to acquire edit lock',
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On unmount, best-effort release the lock.
  useEffect(() => {
    return () => {
      if (lock.status === 'acquired') {
        releaseLock.mutate();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lock.status]);

  // Derived: does the trainer have at least one edit?
  const hasEdits = useMemo(() => {
    return (
      Object.keys(edits.sections).length > 0 ||
      Object.keys(edits.tasks).length > 0 ||
      Object.keys(edits.steps).length > 0
    );
  }, [edits]);

  const canSubmit =
    lock.status === 'acquired' &&
    hasEdits &&
    rationale.trim().length >= 10 &&
    submitState.kind !== 'submitting';

  // ── Edit handlers ────────────────────────────────────────────────────────
  const editSection = useCallback(
    (sectionId: string, patch: Partial<EditState['sections'][string]>) => {
      setEdits((prev) => ({
        ...prev,
        sections: {
          ...prev.sections,
          [sectionId]: { ...(prev.sections[sectionId] ?? {}), ...patch },
        },
      }));
    },
    []
  );
  const editTask = useCallback(
    (taskId: string, patch: Partial<EditState['tasks'][string]>) => {
      setEdits((prev) => ({
        ...prev,
        tasks: {
          ...prev.tasks,
          [taskId]: { ...(prev.tasks[taskId] ?? {}), ...patch },
        },
      }));
    },
    []
  );
  const editStep = useCallback(
    (stepId: string, patch: Partial<EditState['steps'][string]>) => {
      setEdits((prev) => ({
        ...prev,
        steps: {
          ...prev.steps,
          [stepId]: { ...(prev.steps[stepId] ?? {}), ...patch },
        },
      }));
    },
    []
  );

  // ── Build operations[] from accumulated edits ───────────────────────────
  const buildOperations = useCallback((): SopProposalOperation[] => {
    const ops: SopProposalOperation[] = [];

    for (const [stepId, patch] of Object.entries(edits.steps)) {
      if (patch.description !== undefined || patch.isSafetyCritical !== undefined) {
        ops.push({
          op: 'step_update',
          stepId,
          ...(patch.description !== undefined && { description: patch.description }),
          ...(patch.isSafetyCritical !== undefined && {
            isSafetyCritical: patch.isSafetyCritical,
          }),
        });
      }
    }
    for (const [taskId, patch] of Object.entries(edits.tasks)) {
      if (patch.title !== undefined || patch.description !== undefined) {
        ops.push({
          op: 'task_update',
          taskId,
          ...(patch.title !== undefined && { title: patch.title }),
          ...(patch.description !== undefined && { description: patch.description }),
        });
      }
    }
    for (const [sectionId, patch] of Object.entries(edits.sections)) {
      if (
        patch.title !== undefined ||
        patch.description !== undefined ||
        patch.whenToDoTrigger !== undefined
      ) {
        ops.push({
          op: 'section_update',
          sectionId,
          ...(patch.title !== undefined && { title: patch.title }),
          ...(patch.description !== undefined && { description: patch.description }),
          ...(patch.whenToDoTrigger !== undefined && {
            whenToDoTrigger: patch.whenToDoTrigger,
          }),
        });
      }
    }
    return ops;
  }, [edits]);

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!canSubmit) return;
    const operations = buildOperations();
    if (operations.length === 0) return;
    const changes: SopProposalChanges = { operations };

    setSubmitState({ kind: 'submitting' });
    try {
      await createProposal.mutateAsync({
        changes,
        rationale: rationale.trim(),
        evidence: [],
      });
      setSubmitState({ kind: 'success' });
      // Give the success toast a beat to render, then close.
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setSubmitState({
        kind: 'error',
        message:
          err instanceof Error
            ? err.message
            : 'Failed to create proposal. Please try again.',
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-3xl mx-4 bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-amber-50 flex items-center justify-center">
              <AlertTriangle size={18} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Edit SOP (Advanced)
              </h2>
              <p className="text-xs text-gray-500">
                Manual proposal — enters the same review queue as AI-authored
                proposals. Requires approval from another trainer.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Lock state banner */}
        <LockBanner lock={lock} />

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {lock.status === 'acquired' && (
            <div className="space-y-5">
              {/* Sections → Tasks → Steps */}
              {(sop.sections ?? []).length === 0 ? (
                <div className="rounded-xl border border-gray-200 p-6 text-sm text-gray-500 bg-gray-50">
                  This SOP has no processed structure yet. The fallback editor
                  is only available on SOPs with sections/tasks/steps extracted.
                </div>
              ) : (
                (sop.sections ?? []).map((section) => (
                  <SectionEditor
                    key={section.id}
                    section={section}
                    edits={edits}
                    onEditSection={editSection}
                    onEditTask={editTask}
                    onEditStep={editStep}
                  />
                ))
              )}

              {/* Rationale */}
              <div className="border-t border-gray-100 pt-5">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Rationale <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Describe why this change is needed. Visible to the approving
                  trainer during review. Minimum 10 characters.
                </p>
                <textarea
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  rows={3}
                  placeholder="e.g. Recent near-miss incident on Line 33 — adding explicit safety gate before step 3.1."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none text-sm bg-white resize-none"
                  maxLength={4000}
                />
                <div className="text-xs text-gray-400 mt-1 text-right">
                  {rationale.length} / 4000
                </div>
              </div>

              {/* Image uploader stub */}
              <div className="rounded-xl border border-dashed border-gray-300 p-4 bg-gray-50">
                <div className="flex items-start gap-3">
                  <ImagePlus className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">
                      Attach images
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Image upload is not yet wired. Available after Chunk 23.4
                      ships evidence harvesting. Your rationale and text edits
                      will submit without images.
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled
                    title="Not yet available — Chunk 23.4"
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-400 bg-white border border-gray-200 cursor-not-allowed"
                  >
                    Upload (stub)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <SubmitStatus state={submitState} />
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 px-5 py-2 bg-[#00674a] text-white font-semibold rounded-lg hover:bg-[#005540] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {submitState.kind === 'submitting' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Generate as draft proposal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Lock state banner
// ==========================================================================

function LockBanner({ lock }: { lock: LockState }) {
  if (lock.status === 'acquiring') {
    return (
      <div className="px-6 py-2.5 bg-blue-50 border-b border-blue-100 flex items-center gap-2 text-sm text-blue-700">
        <Loader2 className="w-4 h-4 animate-spin" />
        Acquiring edit lock...
      </div>
    );
  }
  if (lock.status === 'conflict') {
    const name = lock.otherUser?.name ?? 'another trainer';
    const expiresAt = lock.expiresAt ? formatTime(lock.expiresAt) : 'unknown';
    return (
      <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-200 flex items-start gap-2 text-sm text-amber-800">
        <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">{name} is editing this SOP</p>
          <p className="text-xs text-amber-700">
            Lock expires at {expiresAt}. Try again after then, or ask them to
            close their editor.
          </p>
        </div>
      </div>
    );
  }
  if (lock.status === 'error') {
    return (
      <div className="px-6 py-2.5 bg-red-50 border-b border-red-200 flex items-start gap-2 text-sm text-red-700">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Could not open the editor</p>
          <p className="text-xs">{lock.errorMessage ?? 'Unknown error'}</p>
        </div>
      </div>
    );
  }
  // Acquired
  const expiresAt = lock.expiresAt ? formatTime(lock.expiresAt) : '';
  return (
    <div className="px-6 py-2.5 bg-[#c5e8c2]/40 border-b border-[#c5e8c2] flex items-center gap-2 text-sm text-[#00674a]">
      <Info className="w-4 h-4 flex-shrink-0" />
      You hold the edit lock until {expiresAt}.
    </div>
  );
}

// ==========================================================================
// Submit status indicator
// ==========================================================================

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

function SubmitStatus({ state }: { state: SubmitState }) {
  if (state.kind === 'idle') return <div />;
  if (state.kind === 'submitting') {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Submitting...
      </div>
    );
  }
  if (state.kind === 'success') {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-600">
        <CheckCircle2 className="w-4 h-4" />
        Draft submitted — awaits approval by another trainer.
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-sm text-red-600">
      <AlertTriangle className="w-4 h-4" />
      {state.message}
    </div>
  );
}

// ==========================================================================
// Section / Task / Step editors (shell implementation)
// ==========================================================================

interface SectionEditorProps {
  section: NonNullable<SopDetail['sections']>[number];
  edits: EditState;
  onEditSection: (
    sectionId: string,
    patch: Partial<EditState['sections'][string]>
  ) => void;
  onEditTask: (
    taskId: string,
    patch: Partial<EditState['tasks'][string]>
  ) => void;
  onEditStep: (stepId: string, patch: Partial<EditState['steps'][string]>) => void;
}

function SectionEditor({
  section,
  edits,
  onEditSection,
  onEditTask,
  onEditStep,
}: SectionEditorProps) {
  const sectionEdit = edits.sections[section.id] ?? {};
  const currentTitle = sectionEdit.title ?? section.title;
  const currentDesc = sectionEdit.description ?? section.description ?? '';
  const currentTrigger =
    sectionEdit.whenToDoTrigger ?? section.whenToDoTrigger ?? '';

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-white">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Job {section.sectionNumber} — Title
        </label>
        <input
          type="text"
          value={currentTitle}
          onChange={(e) => onEditSection(section.id, { title: e.target.value })}
          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none bg-white"
        />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Description
        </label>
        <textarea
          value={currentDesc}
          onChange={(e) =>
            onEditSection(section.id, { description: e.target.value })
          }
          rows={2}
          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none bg-white resize-none"
        />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          When to do (max 80 chars)
        </label>
        <input
          type="text"
          value={currentTrigger}
          maxLength={80}
          onChange={(e) =>
            onEditSection(section.id, { whenToDoTrigger: e.target.value })
          }
          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none bg-white"
        />
      </div>
      {/* Tasks */}
      <div className="space-y-3 pl-4 border-l-2 border-gray-100">
        {(section.tasks ?? []).map((task) => (
          <TaskEditor
            key={task.id}
            task={task}
            edits={edits}
            onEditTask={onEditTask}
            onEditStep={onEditStep}
          />
        ))}
      </div>
    </div>
  );
}

interface TaskEditorProps {
  task: NonNullable<SopDetail['sections']>[number]['tasks'][number];
  edits: EditState;
  onEditTask: (
    taskId: string,
    patch: Partial<EditState['tasks'][string]>
  ) => void;
  onEditStep: (stepId: string, patch: Partial<EditState['steps'][string]>) => void;
}

function TaskEditor({ task, edits, onEditTask, onEditStep }: TaskEditorProps) {
  const taskEdit = edits.tasks[task.id] ?? {};
  const currentTitle = taskEdit.title ?? task.title;
  const currentDesc = taskEdit.description ?? task.description ?? '';
  return (
    <div className="space-y-2">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Task {task.taskNumber} — Title
        </label>
        <input
          type="text"
          value={currentTitle}
          onChange={(e) => onEditTask(task.id, { title: e.target.value })}
          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none bg-white"
        />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Description
        </label>
        <textarea
          value={currentDesc}
          onChange={(e) => onEditTask(task.id, { description: e.target.value })}
          rows={2}
          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none bg-white resize-none"
        />
      </div>
      {/* Steps */}
      <div className="space-y-2 pl-4 border-l-2 border-gray-100">
        {(task.steps ?? []).map((step) => (
          <StepEditor
            key={step.id}
            step={step}
            edits={edits}
            onEditStep={onEditStep}
          />
        ))}
      </div>
    </div>
  );
}

interface StepEditorProps {
  step: NonNullable<SopDetail['sections']>[number]['tasks'][number]['steps'][number];
  edits: EditState;
  onEditStep: (stepId: string, patch: Partial<EditState['steps'][string]>) => void;
}

function StepEditor({ step, edits, onEditStep }: StepEditorProps) {
  const stepEdit = edits.steps[step.id] ?? {};
  const currentDesc = stepEdit.description ?? step.description;
  const currentSafety = stepEdit.isSafetyCritical ?? step.isSafetyCritical;
  return (
    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
      <div className="flex items-start gap-2">
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#00674a]/10 text-[#00674a] flex items-center justify-center text-xs font-semibold">
          {step.stepNumber}
        </span>
        <textarea
          value={currentDesc}
          onChange={(e) => onEditStep(step.id, { description: e.target.value })}
          rows={2}
          className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none bg-white resize-none"
        />
      </div>
      <div className="flex items-center gap-2 pl-7">
        <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={currentSafety}
            onChange={(e) =>
              onEditStep(step.id, { isSafetyCritical: e.target.checked })
            }
            className="rounded border-gray-300 text-[#00674a] focus:ring-[#00674a] bg-white"
          />
          Safety-critical
        </label>
      </div>
    </div>
  );
}

// ==========================================================================
// Utilities
// ==========================================================================

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
